export const API_BASE_URL = (process.env.VUE_APP_URL || 'http://localhost:3000').replace(/\/+$/, '')

function getAuthHeaders() {
  const token = JSON.parse(localStorage.getItem('access_token'))
  const headers = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

function handleResponse(response) {
  if (!response.ok) {
    return response.json().then(err => {
      throw err
    })
  }
  return response.json()
}

async function safeFetch(input, init) {
  try {
    const res = await fetch(input, init)
    return handleResponse(res)
  } catch (err) {
    const url = (input && input.href) || input || ''
    console.error('[bidApi] Network fetch failed for', url, err)
    throw { error: 'Network Error', message: `Failed to fetch ${url}: ${err?.message || err}` }
  }
}

// Normalize raw Opportunity objects from the backend into the
// flatter shape expected by the BID UI (tenderRefNo, title,
// client name string, sector name, department name, country, etc.).
function normalizeOpportunity(raw) {
  if (!raw || typeof raw !== 'object') return raw

  const client = raw.client || {}
  const sector = raw.sector || client.sector || {}
  const department = raw.department || {}

  return {
    ...raw,
    // aliases for existing fields
    id: raw.id,
    tenderRefNo: raw.tenderRefNo || raw.tenderReference,
    referenceNo: raw.referenceNo || raw.tenderReference,
    title: raw.title || raw.tenderTitle,
    client: raw.clientName || client.name,
    sector: raw.sectorName || sector.name,
    department: raw.departmentName || department.name,
    country: raw.country || client.country,
    submissionDeadline: raw.submissionDeadline,
    clarificationDeadline: raw.clarificationDeadline,
    preBidMeeting: raw.preBidMeeting || raw.preBidMeetingDate,
    siteVisit: raw.siteVisit || raw.siteVisitDate,
  }
}

export const bidApi = {
  // Clients
  listClients: () => {
    return safeFetch(`${API_BASE_URL}/clients`, {
      headers: getAuthHeaders(),
    }).then((data) => {
      const list = data && typeof data === 'object' && (data.data || data.items || data.rows)
        ? data.data || data.items || data.rows
        : data
      return Array.isArray(list) ? list : []
    })
  },

  // Opportunities (Module 2)
  listOpportunities: (params) => {
    const url = new URL(API_BASE_URL + '/opportunities')
    if (params) Object.keys(params).forEach(key => {
      const v = params[key]
      if (v !== undefined && v !== null && v !== '') url.searchParams.append(key, params[key])
    })
    return safeFetch(url, {
      headers: getAuthHeaders(),
    }).then((data) => {
      const list = data.data || data.items || data.rows || data
      const normalized = Array.isArray(list) ? list.map(normalizeOpportunity) : []

      // Preserve any wrapper stats if present, otherwise just
      // return the normalized list directly.
      if (data && typeof data === 'object' && (data.data || data.items || data.rows || data.stats)) {
        return {
          ...data,
          data: normalized,
        }
      }

      return normalized
    })
  },

  getOpportunity: (id) => {
    return safeFetch(`${API_BASE_URL}/opportunities/${id}`, {
      headers: getAuthHeaders(),
    }).then((data) => {
      const op = data.data || data
      const normalized = normalizeOpportunity(op)
      if (data && typeof data === 'object' && data.data) {
        return { ...data, data: normalized }
      }
      return normalized
    })
  },

  createOpportunity: (payload) => {
    const body = {
      tenderReference: payload.tenderRefNo || payload.tenderReference || '',
      tenderTitle: payload.title || payload.tenderTitle || '',
      clientId: payload.clientId || (payload.client && payload.client.id) || undefined,
      departmentId: payload.departmentId || (payload.department && payload.department.id) || undefined,
      sectorId: payload.sectorId || (payload.sector && payload.sector.id) || undefined,
      tenderType: payload.tenderType || payload.tenderTypeName || '',
      tenderTypeId: payload.tenderTypeId || undefined,
      estimatedValue: payload.estimatedValue,
      // pre-bid / site visit / clarification dates
      preBidMeetingDate: payload.preBidMeeting
        ? new Date(payload.preBidMeeting).toISOString()
        : undefined,
      siteVisitDate: payload.siteVisit ? new Date(payload.siteVisit).toISOString() : undefined,
      clarificationDeadline: payload.clarificationDeadline
        ? new Date(payload.clarificationDeadline).toISOString()
        : undefined,
      source: payload.source,
      submissionDeadline: payload.submissionDeadline
        ? new Date(payload.submissionDeadline).toISOString()
        : undefined,
      bidSecurityRequired: payload.bidSecurityRequired,
    }

    return safeFetch(`${API_BASE_URL}/opportunities`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    })
  },

  updateOpportunity: (id, payload) => {
    const body = {
      tenderReference: payload.tenderRefNo || payload.tenderReference,
      tenderTitle: payload.title || payload.tenderTitle,
      clientId: payload.clientId || (payload.client && payload.client.id),
      departmentId: payload.departmentId || (payload.department && payload.department.id),
      sectorId: payload.sectorId || (payload.sector && payload.sector.id),
      tenderType: payload.tenderType || payload.tenderTypeName || '',
      tenderTypeId: payload.tenderTypeId || undefined,
      estimatedValue: payload.estimatedValue,
      // pre-bid / site visit / clarification dates
      preBidMeetingDate: payload.preBidMeeting
        ? new Date(payload.preBidMeeting).toISOString()
        : undefined,
      siteVisitDate: payload.siteVisit ? new Date(payload.siteVisit).toISOString() : undefined,
      clarificationDeadline: payload.clarificationDeadline
        ? new Date(payload.clarificationDeadline).toISOString()
        : undefined,
      source: payload.source,
      submissionDeadline: payload.submissionDeadline
        ? new Date(payload.submissionDeadline).toISOString()
        : undefined,
      bidSecurityRequired: payload.bidSecurityRequired,
    }

    return safeFetch(`${API_BASE_URL}/opportunities/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    })
  },

  // Qualification (Stage B)
  updateQualification: (id, payload) => {
    const body = {
      opportunityId: id,
      capacityAvailable:
        typeof payload.capacityAvailable === 'boolean'
          ? payload.capacityAvailable
          : payload.capacity
          ? payload.capacity !== 'INSUFFICIENT'
          : true,
      riskAssessment:
        payload.riskAssessment ||
        [payload.riskLevel, payload.remarks].filter(Boolean).join(' - ') ||
        'Not specified',
      budgetFeasibility:
        typeof payload.budgetFeasibility === 'boolean'
          ? payload.budgetFeasibility
          : payload.budgetFeasibility === 'FEASIBLE' || payload.budgetFeasibility === 'TIGHT',
    }

    return safeFetch(`${API_BASE_URL}/qualification`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    })
  },

  // Go / No-Go (Stage C)
  updateDecision: (id, payload) => {
    if (payload && payload.status === 'APPROVED') {
      const body = {
        approvedById: payload.approvedById || undefined,
        comments: payload.remarks || payload.comments || undefined,
      }
      return safeFetch(`${API_BASE_URL}/opportunities/${id}/approve`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      })
    }

    // For now, only server-side approve is supported; other
    // decisions are handled client-side until backend endpoints exist.
    return Promise.resolve({ status: payload.status })
  },

  // Bid Preparation (Module 3)
  // Accepts payload with status, preBidHeld, siteVisitDone, clarificationsSent, notes,
  // preBidMeeting, siteVisit (dates) and sends them to the backend.
  updatePreparation: async (id, payload = {}) => {
    const body = {
      opportunityId: id,
      status: payload.status || 'NOT_STARTED',
      preBidHeld:
        typeof payload.preBidHeld === 'boolean'
          ? payload.preBidHeld
          : payload.preBidHeld === 'true' || payload.preBidHeld === 'YES'
          ? true
          : false,
      siteVisitDone:
        typeof payload.siteVisitDone === 'boolean'
          ? payload.siteVisitDone
          : payload.siteVisitDone === 'true' || payload.siteVisitDone === 'YES'
          ? true
          : false,
      clarificationsSent:
        typeof payload.clarificationsSent === 'boolean'
          ? payload.clarificationsSent
          : payload.clarificationsSent === 'true' || payload.clarificationsSent === 'YES'
          ? true
          : false,
      notes: payload.notes || payload.preparationNotes || '',
      preBidMeetingDate: payload.preBidMeeting ? new Date(payload.preBidMeeting).toISOString() : undefined,
      siteVisitDate: payload.siteVisit ? new Date(payload.siteVisit).toISOString() : undefined,
    }

    const res = await safeFetch(`${API_BASE_URL}/bid-preparation`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    })

    // Normalize response to include `.preparation` for the UI layers that expect it
    const prep = (res && typeof res === 'object' && (res.preparation || res.data || res.item))
      ? res.preparation || res.data || res.item
      : res

    // PATCH the record to persist all updatable fields
    if (prep && prep.id) {
      try {
        const patchBody = {}
        if (payload.ownerId !== undefined) patchBody.ownerId = payload.ownerId
        if (payload.status !== undefined) patchBody.status = payload.status
        if (payload.preBidHeld !== undefined) patchBody.preBidHeld = payload.preBidHeld
        if (payload.siteVisitDone !== undefined) patchBody.siteVisitDone = payload.siteVisitDone
        if (payload.clarificationsSent !== undefined) patchBody.clarificationsSent = payload.clarificationsSent
        if (payload.notes !== undefined) patchBody.notes = payload.notes
        const patched = await safeFetch(`${API_BASE_URL}/bid-preparation/${prep.id}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(patchBody),
        })
        const updatedPrep = patched && (patched.data || patched.preparation) ? (patched.data || patched.preparation) : patched
        return { preparation: updatedPrep || prep }
      } catch (err) {
        // swallow patch error but return the ensured prep
        console.error('[bidApi] failed to patch preparation', err)
        return { preparation: prep }
      }
    }

    return { preparation: prep }
  },

  // All bid preparations (for overview page)
  getAllPreparations: async () => {
    const data = await safeFetch(`${API_BASE_URL}/bid-preparation`, { headers: getAuthHeaders() })
    // Backend returns a plain array; also handle wrapped { data: [...] } shape
    if (Array.isArray(data)) return data
    const list = data && (data.data || data.items || data.rows)
    return Array.isArray(list) ? list : []
  },

  // Bid Preparation Checklists
  listChecklists: async (preparationId) => {
    if (!preparationId) return []
    const url = `${API_BASE_URL}/bid-preparation/${preparationId}/checklists`
    return safeFetch(url, { headers: getAuthHeaders() }).then((data) => {
      const list = data && (data.data || data.items || data.rows) ? (data.data || data.items || data.rows) : data
      return Array.isArray(list) ? list : []
    })
  },

  createChecklist: async (preparationId, itemName) => {
    if (!preparationId) throw new Error('preparationId required')
    const body = { preparationId, itemName }
    return safeFetch(`${API_BASE_URL}/bid-preparation/${preparationId}/checklists`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    })
  },

  updateChecklist: async (checklistId, payload = {}) => {
    if (!checklistId) throw new Error('checklistId required')
    const body = { ...payload }
    return safeFetch(`${API_BASE_URL}/bid-checklists/${checklistId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    })
  },

  deleteChecklist: async (checklistId) => {
    if (!checklistId) return Promise.resolve()
    return safeFetch(`${API_BASE_URL}/bid-checklists/${checklistId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    })
  },

  // Loss Records
  listLossRecords: async (params) => {
    const url = new URL(API_BASE_URL + '/loss')
    if (params) Object.keys(params).forEach(key => {
      const v = params[key]
      if (v !== undefined && v !== null && v !== '') url.searchParams.append(key, params[key])
    })
    return safeFetch(url, { headers: getAuthHeaders() }).then((data) => {
      const list = data && (data.data || data.items || data.rows) ? (data.data || data.items || data.rows) : data
      return Array.isArray(list) ? list : []
    })
  },

  createLossRecord: async (payload) => {
    return safeFetch(`${API_BASE_URL}/loss`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    })
  },

  updateLossRecord: async (id, payload) => {
    return safeFetch(`${API_BASE_URL}/loss/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    })
  },

  deleteLossRecord: async (id) => {
    return safeFetch(`${API_BASE_URL}/loss/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    })
  },

  // Submission (Module 4)
  updateSubmission: (id, payload) => {
    const body = {
      opportunityId: id,
      submissionMethod: payload.submissionMethod || payload.method || '',
      bidAmount: Number(payload.bidAmount || 0),
      marginPercentage: Number(payload.margin || 0),
      bidPreparationCost: Number(payload.bidPreparationCost || payload.preparationCost || 0),
      bidValidityExpiry: (payload.bidValidityExpiry || payload.validityExpiry)
        ? new Date(payload.bidValidityExpiry || payload.validityExpiry).toISOString()
        : new Date().toISOString(),
      proofUrl: payload.proofUrl || payload.proof || '',
      submittedById: payload.submittedById || undefined,
    }

    return safeFetch(`${API_BASE_URL}/bid-submission`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    })
  },

  // Award & Contract (Module 5)
  updateAward: async (id, payload) => {
    if (payload && payload.status === 'AWARDED') {
      const body = {
        opportunityId: id,
        awardDate: payload.awardDate
          ? new Date(payload.awardDate).toISOString()
          : new Date().toISOString(),
        contractValue: Number(payload.contractValue || 0),
        contractDuration: Number(payload.contractDuration || 0),
        performanceBond:
          payload.performanceBond === true ||
          payload.performanceBond === 'YES' ||
          payload.performanceBond === 'Yes',
        advanceGuarantee:
          payload.advanceGuarantee === true ||
          payload.advancePaymentGuarantee === 'YES' ||
          payload.advancePaymentGuarantee === 'Yes',
        projectOwnerId: payload.projectOwnerId || (payload.projectOwner && payload.projectOwner.id) || undefined,
      }

      return safeFetch(`${API_BASE_URL}/contracts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      })
    }

    if (payload && payload.status === 'LOST') {
      // 1. Update opportunity status to LOST
      await safeFetch(`${API_BASE_URL}/opportunities/${id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: 'LOST' }),
      })

      // 2. If a BidSubmission exists, create a LossRecord linked to it
      if (payload.submissionId) {
        try {
          await safeFetch(`${API_BASE_URL}/loss`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              submissionId: payload.submissionId,
              lossReason: payload.lossReason || 'Not specified',
              competitor: payload.competitor || undefined,
              lessonsLearned: payload.lessonsLearned || undefined,
            }),
          })
        } catch (e) {
          // Loss record creation is best-effort; don't block the status update
          console.error('[bidApi] Failed to create loss record', e)
        }
      }

      return { status: 'LOST' }
    }

    return Promise.resolve({ status: payload.status })
  },

  // Analytics (Module 6) - optional dashboard
  getAnalyticsSummary: (params) => {
    const url = new URL(API_BASE_URL + '/bd/analytics/summary')
    if (params) Object.keys(params).forEach(key => {
      const v = params[key]
      if (v !== undefined && v !== null && v !== '') url.searchParams.append(key, params[key])
    })
    return safeFetch(url, {
      headers: getAuthHeaders(),
    })
  },
  generateAnalyticsReport: async (params) => {
    const url = new URL(API_BASE_URL + '/bd/analytics/report')
    if (params) Object.keys(params).forEach(key => {
      const v = params[key]
      if (v !== undefined && v !== null && v !== '') url.searchParams.append(key, params[key])
    })
    const headers = getAuthHeaders()
    // If Content-Type exists, remove it for blob downloads
    if (headers['Content-Type']) delete headers['Content-Type']
    try {
      const res = await fetch(url, { headers })
      if (!res.ok) {
        // try parse json error
        let err
        try { err = await res.json() } catch (e) { err = { message: 'Failed to generate report' } }
        throw err
      }
      const blob = await res.blob()
      const disposition = res.headers.get('content-disposition') || ''
      return { blob, disposition }
    } catch (err) {
      console.error('[bidApi] generateAnalyticsReport failed', err)
      throw err
    }
  },
  // Contracts
  listContracts: (params) => {
    const url = new URL(API_BASE_URL + '/contracts')
    if (params) Object.keys(params).forEach(key => {
      const v = params[key]
      if (v !== undefined && v !== null && v !== '') url.searchParams.append(key, params[key])
    })
    return safeFetch(url, { headers: getAuthHeaders() }).then((data) => {
      const list = data.data || data.items || data.rows || data
      return Array.isArray(list) ? list : []
    })
  },
  getContract: (id) => {
    if (!id) return Promise.resolve(null)
    return safeFetch(`${API_BASE_URL}/contracts/${id}`, { headers: getAuthHeaders() }).then((data) => {
      return data && data.data ? data.data : data
    })
  },
}

export default bidApi
